import { type UserDataType } from '@app-types/UserDataType'

import useFontSizes from '@hooks/useFontSizes'
import { ChangeEvent } from 'react'
import { FaCircleInfo, FaCircleMinus, FaCirclePlus } from 'react-icons/fa6'
import { kuranTypes } from '../consts'
import Tooltip from '../Tooltip/Tooltip'
import KuranSample from './KuranSample'
import SegmentedToggle from './SegmentedToggle'

const getInfoTextByType = (type: UserDataType['kuranType']) => {
  switch (type) {
    case 'default':
      return "Bu mod ile Kuran-ı Kerim'i kelime mealli olarak da okuyabilirsiniz."
    case 'hayrat':
      return 'Hayrat formatında meal seçeneği bulunmamaktadır.'
    case 'learning':
      return 'Bu mod ile kelimelerin üzerine tıklayarak onları dinleyebilir, ayet sonlarındaki numaraların üzerine tıklayarak ayeti komple dinleyebilirsiniz.'
    case 'only-meal':
      return "Bu mod ile Kuran-ı Kerim'in sadece mealini okuyabilirsiniz."
    case 'mealli':
      return "Bu mod ile Kuran-ı Kerim'i ayet ayet mealleriyle birlikte okuyabilirsiniz."
    default:
      return ''
  }
}

const kelimeMealliModes: Partial<UserDataType['kuranType']>[] = [
  'default',
  'learning',
  'mealli',
]

export type KuranSettingsProps = {
  userData: UserDataType
  handleInputChange: (_event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
}

export default function KuranSettings({
  userData,
  handleInputChange,
}: KuranSettingsProps): React.ReactNode {
  const { kuranFontSize, kuranMealFontSize, setFontSize } = useFontSizes()
  return (
    <>
      <KuranSample userData={userData} />
      <div className="flex justify-between items-center my-4 py-3 border-b border-main space-x-2">
        <label htmlFor="kuranType" className="font-bold text-main shrink-0">
          Kuran Modu
        </label>
        <select
          onChange={handleInputChange}
          value={userData.kuranType}
          name="kuranType"
          id="kuranType"
          className="bg-surface-subtle text-main px-4 py-2 border border-main rounded-xl w-36 sm:w-42 outline-none focus:ring-2 focus:ring-brand-secondary/50 font-medium appearance-none text-center"
        >
          {Object.entries(kuranTypes).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </select>
      </div>
      <span className="text-xs text-muted block mb-2 px-1">
        <i>{getInfoTextByType(userData.kuranType)}</i>
      </span>
      {userData.kuranType !== 'hayrat' && (
        <div className="flex justify-between items-center my-4 py-3 border-b border-main space-x-2">
          <label htmlFor="kuranMealSource" className="font-bold text-main shrink-0">
            Meal Kaynağı
          </label>
          <select
            onChange={handleInputChange}
            value={userData.kuranMealSource}
            name="kuranMealSource"
            id="kuranMealSource"
            className="bg-surface-subtle text-main px-4 py-2 border border-main rounded-xl w-36 sm:w-42 outline-none focus:ring-2 focus:ring-brand-secondary/50 font-medium appearance-none text-center"
          >
            <option value="muhtasar_tefsir">Muhtasar Tefsir</option>
            <option value="meal_diyanet">Diyanet Vakfı Meali</option>
          </select>
        </div>
      )}

      {kelimeMealliModes.includes(userData.kuranType) && (
        <>
          <div className="flex justify-between items-center my-4 py-3 border-b border-main">
            <div className="flex items-center gap-1 shrink-0">
              <label htmlFor="kuranMealType" className="font-bold text-main">
                Kelime Meali
              </label>
              <Tooltip
                content="Mealsiz opsiyonda ayet sonuna tıklayarak ayet mealini, kelime üzerine
              tıklayarak kelime mealini görebilirsiniz. Dilerseniz bunu aşağıdaki
              seçeneklerden kapatabilirsiniz."
              >
                <FaCircleInfo className="text-muted" />
              </Tooltip>
            </div>
            <SegmentedToggle
              value={userData.kuranMealType === 'show'}
              onChange={(val) => 
                handleInputChange({ target: { name: 'kuranMealType', value: val ? 'show' : 'hide' } } as React.ChangeEvent<any>)
              }
            />
          </div>

          <div className="flex justify-between items-center my-4 py-3 border-b border-main">
            <label htmlFor="kuranTooltip" className="font-bold text-main">
              Tıklandığında Meal
            </label>
            <SegmentedToggle
              value={userData.kuranTooltip === 'show'}
              onChange={(val) => 
                handleInputChange({ target: { name: 'kuranTooltip', value: val ? 'show' : 'hide' } } as React.ChangeEvent<any>)
              }
            />
          </div>
          <div className="flex justify-between items-center my-4 py-3 border-b border-main">
            <label htmlFor="autoScroll" className="font-bold text-main">
              Dinlerken Otomatik Takip
            </label>
            <SegmentedToggle
              value={userData.autoScroll === 'show'}
              onChange={(val) => 
                handleInputChange({ target: { name: 'autoScroll', value: val ? 'show' : 'hide' } } as React.ChangeEvent<any>)
              }
            />
          </div>

          <div className="flex justify-between items-center my-4 py-3 border-b border-main space-x-4">
            <strong className="text-main">Kuran Arapça Boyutu</strong>
            <div className="flex bg-surface-subtle border border-main rounded-xl p-1 gap-1">
              <button
                className="w-12 h-10 flex flex-center rounded-lg text-muted hover:bg-main hover:text-main hover:shadow-sm transition-all"
                onClick={() => {
                  setFontSize(kuranFontSize - 1, 'kuranFontSize')
                }}
                aria-label="Kuran Arapça boyutunu küçült"
              >
                <FaCircleMinus />
              </button>
              <div className="w-[2px] bg-main my-2 mx-1"></div>
              <button
                className="w-12 h-10 flex flex-center rounded-lg text-muted hover:bg-main hover:text-main hover:shadow-sm transition-all"
                onClick={() => {
                  setFontSize(kuranFontSize + 1, 'kuranFontSize')
                }}
                aria-label="Kuran Arapça boyutunu büyüt"
              >
                <FaCirclePlus />
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center my-4 py-3 border-b border-main space-x-4">
            <strong className="text-main">Kuran Meal Boyutu</strong>
            <div className="flex bg-surface-subtle border border-main rounded-xl p-1 gap-1">
              <button
                className="w-12 h-10 flex flex-center rounded-lg text-muted hover:bg-main hover:text-main hover:shadow-sm transition-all"
                onClick={() => {
                  setFontSize(kuranMealFontSize - 1, 'kuranMealFontSize')
                }}
                aria-label="Kuran Meal boyutunu küçült"
              >
                <FaCircleMinus />
              </button>
              <div className="w-[2px] bg-main my-2 mx-1"></div>
              <button
                className="w-12 h-10 flex flex-center rounded-lg text-muted hover:bg-main hover:text-main hover:shadow-sm transition-all"
                onClick={() => {
                  setFontSize(kuranMealFontSize + 1, 'kuranMealFontSize')
                }}
                aria-label="Kuran Meal boyutunu büyüt"
              >
                <FaCirclePlus />
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
